import React, { createContext, useContext, useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('pantass_token'));
  const [loading, setLoading] = useState(true);
  const [planLimits, setPlanLimits] = useState(null);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        fetchPlanLimits();
      } else {
        logout();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanLimits = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/plan-limits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPlanLimits(data);
      }
    } catch (e) { console.error(e); }
  };

  const login = async (email, password) => {
    const res = await fetch(`${BACKEND_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    if (res.status === 429) throw new Error('Terlalu banyak percubaan. Sila tunggu sebentar.');
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Login failed');
    localStorage.setItem('pantass_token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const register = async (name, email, password, school_name, ref) => {
    const res = await fetch(`${BACKEND_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password, school_name, ref: ref || '' })
    });
    if (res.status === 429) throw new Error('Terlalu banyak percubaan. Sila tunggu sebentar.');
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Registration failed');
    localStorage.setItem('pantass_token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('pantass_token');
    setToken(null);
    setUser(null);
    setPlanLimits(null);
  };

  const canPlay = () => {
    if (!user) return true; // guest can play (no enforcement without login)
    if (!planLimits) return true;
    return planLimits.can_play;
  };

  const canAccessSubject = (subjectIndex) => {
    if (!user) return true;
    const plan = user.plan || 'free';
    const limit = PLAN_LIMITS_LOCAL[plan]?.subjects || 2;
    return subjectIndex < limit;
  };

  const canAccessDifficulty = (difficulty) => {
    if (!user) return true;
    const plan = user.plan || 'free';
    const maxDiff = PLAN_LIMITS_LOCAL[plan]?.max_difficulty || 'easy';
    const order = { easy: 0, medium: 1, hard: 2 };
    return order[difficulty] <= order[maxDiff];
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, planLimits,
      login, register, logout, fetchUser, fetchPlanLimits,
      canPlay, canAccessSubject, canAccessDifficulty,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

const PLAN_LIMITS_LOCAL = {
  free: { games_per_month: 25, subjects: 2, max_difficulty: 'easy' },
  basic: { games_per_month: 999999, subjects: 8, max_difficulty: 'medium' },
  pro: { games_per_month: 999999, subjects: 8, max_difficulty: 'hard' },
  school: { games_per_month: 999999, subjects: 8, max_difficulty: 'hard' },
};

export default AuthContext;
